// "My Frames" — the user's saved custom frame library (Elements → Frames).
// Lists per-account saved frames with search, rename, duplicate, delete, reuse,
// and a "Convert PNG to Frame" import that traces a transparent PNG's alpha into
// a vector path. Clicking a frame inserts it onto the canvas via onInsertFrame.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Search, Upload, Loader2, Trash2, Copy, Pencil } from 'lucide-react';
import {
  currentUserId, listCustomFrames, searchCustomFrames,
  renameCustomFrame, duplicateCustomFrame, deleteCustomFrame, saveCustomFrame,
} from './customFrameStore';
import { tracePngSrc } from './tracePng';
import { frameThumbnailDataUrl } from './frameThumbnail';

const MyFramesLibrary = ({ onInsertFrame }) => {
  const userId = currentUserId();
  const [frames, setFrames] = useState([]);
  const [query, setQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const refresh = useCallback((q = query) => {
    setFrames(q ? searchCustomFrames(userId, q) : listCustomFrames(userId));
  }, [userId, query]);

  useEffect(() => { refresh(''); /* initial load */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const thumbOf = (f) => f.thumbnail || frameThumbnailDataUrl(f.framePath, f.frameViewBox?.w || 1, f.frameViewBox?.h || 1);

  const handleImportPng = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const traced = await tracePngSrc(dataUrl);
      if (!traced) { setImporting(false); return; }
      const name = (file.name || 'PNG Frame').replace(/\.[^.]+$/, '');
      saveCustomFrame(userId, {
        name,
        framePath: traced.path,
        frameViewBox: { w: traced.vbW, h: traced.vbH },
        frameSource: 'png',
        thumbnail: frameThumbnailDataUrl(traced.path, traced.vbW, traced.vbH),
      });
      refresh('');
      setQuery('');
    } finally {
      setImporting(false);
    }
  };

  const insert = (f) => onInsertFrame && onInsertFrame({
    framePath: f.framePath,
    frameViewBox: f.frameViewBox || { w: 1, h: 1 },
    frameSource: f.frameSource || 'custom',
  });

  return (
    <div className="mb-2" data-testid="my-frames-library">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">My Frames</h3>
        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          className="flex items-center gap-1 text-[10px] text-violet-600 hover:text-violet-700"
          data-testid="import-png-frame"
          title="Convert a transparent PNG into a frame"
        >
          {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          PNG → Frame
        </button>
        <input ref={fileRef} type="file" accept="image/png" hidden onChange={handleImportPng} />
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); refresh(e.target.value); }}
          placeholder="Search my frames"
          data-testid="my-frames-search"
          className="w-full h-7 pl-7 pr-2 text-xs border border-gray-200 rounded-md"
        />
      </div>

      {frames.length === 0 ? (
        <p className="text-[11px] text-gray-400 py-2">
          No saved frames yet. Select a frame and use “Save as Custom Frame”, or import a PNG.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {frames.map((f) => (
            <div key={f.id} className="group relative" data-testid={`my-frame-${f.id}`}>
              <button
                type="button"
                onClick={() => insert(f)}
                title={f.name}
                className="w-full aspect-square flex items-center justify-center bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all p-2"
              >
                <img src={thumbOf(f)} alt={f.name} className="w-full h-full object-contain" />
              </button>
              <span className="block text-[9px] text-gray-500 text-center truncate px-1 mt-0.5">{f.name}</span>
              <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5 bg-white/90 rounded-md p-0.5 shadow">
                <button type="button" title="Rename" data-testid={`rename-${f.id}`}
                  onClick={() => { const n = window.prompt('Rename frame', f.name); if (n != null) { renameCustomFrame(userId, f.id, n); refresh(); } }}
                  className="p-0.5 text-gray-500 hover:text-gray-800"><Pencil className="w-3 h-3" /></button>
                <button type="button" title="Duplicate" data-testid={`dup-${f.id}`}
                  onClick={() => { duplicateCustomFrame(userId, f.id); refresh(''); setQuery(''); }}
                  className="p-0.5 text-gray-500 hover:text-gray-800"><Copy className="w-3 h-3" /></button>
                <button type="button" title="Delete" data-testid={`del-${f.id}`}
                  onClick={() => { deleteCustomFrame(userId, f.id); refresh(); }}
                  className="p-0.5 text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyFramesLibrary;
